import { memo, useCallback, useMemo, type ReactNode } from "react";

import { externalLink } from "@/routes/_constants/external-link";

import HotelClassIcon from "@/components/ui/icon/hotel-class";
import PriceCheck from "@/components/ui/icon/price-check";
import SearchIcon from "@/components/ui/icon/search";
import SetUp from "@/components/ui/icon/set-up";

/**
 * CHILLNN サポート「売り上げの入金タイミングは？入金最低金額は？」
 * https://support.chillnn.com/faq/host/settlement/4eefffe15c57833bb87c81872212105f
 *
 * ベースURLは externalLink.faqSite（dev/prd で自動切替）を使う。
 * パスは Notion ページ ID で構成されるため、日本語タイトルや旧スラッグは使わない。
 */
const ACCOUNTING_MANUAL_URL = `${externalLink.faqSite}/faq/host/settlement/4eefffe15c57833bb87c81872212105f`;

const OTHER_LINK_LIST: Array<{ name: string; url: string; icon: ReactNode }> = [
	{
		name: "セットアップマニュアル",
		url: "https://chillnn.notion.site/CHILLNN-Manual-4f45718809404c289ad8238c884b0e7e",
		icon: <SetUp />,
	},
	{
		name: "経理担当者向けマニュアル",
		url: ACCOUNTING_MANUAL_URL,
		icon: <PriceCheck />,
	},
	{
		name: "CHILLNN導入事例集",
		url: "https://chillnn.notion.site/6f0d6f268c4c4efe8620e57bf63e4508",
		icon: <HotelClassIcon />,
	},
];

type Props = {
	searchText: string;
	onChangeSearchText: (value: string) => void;
};

export const SearchForm = memo(({ searchText, onChangeSearchText }: Props) => {
	const handleChangeSearchText = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			onChangeSearchText(event.target.value);
		},
		[onChangeSearchText],
	);

	const canShowLinks = useMemo(() => !searchText, [searchText]);

	return (
		<div className="rounded-lg border border-zinc-100 bg-white p-4 lg:p-12">
			<div className="mx-auto mt-5 max-w-md">
				<h2 className="text-center text-2xl font-bold">どんなことにお困りですか？</h2>

				<div className="mt-2 w-full">
					<div className="flex w-full items-center rounded-md border bg-white px-1 shadow outline-none outline">
						<SearchIcon className="w-10" />
						<input
							type="text"
							className="w-full border-none p-3 text-base focus:outline-none"
							value={searchText}
							placeholder="例）プラン、ポリシー 部屋"
							onChange={handleChangeSearchText}
						/>
					</div>
				</div>
			</div>

			{canShowLinks && (
				<div className="mt-8">
					<div className="grid grid-cols-2 gap-2 lg:grid-cols-3">
						{OTHER_LINK_LIST.map(({ name, url, icon }) => (
							<a
								key={name}
								href={url}
								target="_blank"
								rel="noopener noreferrer"
								className="flex cursor-pointer items-center gap-x-1 rounded-lg border border-zinc-200 p-3 hover:opacity-50"
							>
								<span>{icon}</span>
								<span className="text-sm">{name}</span>
							</a>
						))}
					</div>
				</div>
			)}
		</div>
	);
});
